import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCountryById, createCountry, updateCountry } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

export default function AddEditCountry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("countries");

  const [form, setForm] = useState({
    name: "",
    ascii_name: "",
    iso_code_2: "",
    iso_code_3: "",
    phone_code: "",
    currency: "",
    population: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      getCountryById(token, id).then((c) => {
        setForm({
          name: c.name || "",
          ascii_name: c.ascii_name || "",
          iso_code_2: c.iso_code_2 || "",
          iso_code_3: c.iso_code_3 || "",
          phone_code: c.phone_code || "",
          currency: c.currency || "",
          population: c.population ?? "",
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load country")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});

    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Country name is required");
    if (!form.ascii_name.trim()) newErrors.ascii_name = t("validation_ascii_name_required", "ASCII name is required");
    if (!form.iso_code_2.trim() || form.iso_code_2.trim().length !== 2) newErrors.iso_code_2 = t("validation_iso2_length", "ISO-2 code must be 2 letters");
    if (!form.iso_code_3.trim() || form.iso_code_3.trim().length !== 3) newErrors.iso_code_3 = t("validation_iso3_length", "ISO-3 code must be 3 letters");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      ascii_name: form.ascii_name.trim(),
      iso_code_2: form.iso_code_2.trim(),
      iso_code_3: form.iso_code_3.trim(),
      phone_code: form.phone_code.trim(),
      currency: form.currency.trim(),
      population: form.population ? Number(form.population) : 0,
    };

    try {
      if (id) {
        await updateCountry(token, id, payload);
        notify.success(t("notify_updated", "Country updated successfully"));
      } else {
        await createCountry(token, payload);
        notify.success(t("notify_created", "Country created successfully"));
      }
      navigate("/countries");
    } catch (err) {
      const errorMsg = err.data?.error || err.message || tCommon("error_generic", "Something went wrong");
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button
          onClick={() => navigate("/countries")}
          className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400"
        >
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">
            {id ? t("edit_title", "Edit Country") : t("add_title", "Add New Country")}
          </h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "Define country identity and defaults")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <FloatingInput label={t("field_name_label", "Country Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Countries} />
            </div>
            <EntityTranslationsButton entityType="country" entityId={id} defaultName={form.name} />
          </div>
          <FloatingInput label={t("field_ascii_name_label", "ASCII Name")} value={form.ascii_name} onChange={setField("ascii_name")} error={errors.ascii_name} />

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput label={t("field_iso2_label", "ISO Code 2")} value={form.iso_code_2} onChange={setField("iso_code_2")} error={errors.iso_code_2} />
            <FloatingInput label={t("field_iso3_label", "ISO Code 3")} value={form.iso_code_3} onChange={setField("iso_code_3")} error={errors.iso_code_3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FloatingInput label={t("field_phone_code_label", "Phone Code")} value={form.phone_code} onChange={setField("phone_code")} />
            <FloatingInput label={t("field_currency_label", "Currency")} value={form.currency} onChange={setField("currency")} />
          </div>

          <FloatingInput label={t("field_population_label", "Population")} type="number" value={form.population} onChange={setField("population")} />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_country", "Update Country") : t("btn_create_country", "Create Country")}
            </Button>

            <Button variant="secondary" onClick={() => navigate("/countries")}>
              {tCommon("btn_cancel", "Cancel")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
